/**
 * Whole-questionnaire validation for the CMM questionnaire UI.
 *
 * @module pharmaQuestionaire/utils/validateQuestionnaireAnswers
 */
import {
  Answer,
  QuestionForUI,
} from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import { areAnswersEqual, hasMeaningfulValue } from "./answerEquality";

/**
 * Validates the questionnaire answers for the supplied questions.
 *
 * Callers are expected to pass the subset of questions that should be
 * validated (typically the visible chain produced by
 * `getVisibleQuestionChain`); this function does not walk the flow
 * itself so the same chain can be reused for rendering, validation, and
 * logging without recomputing.
 *
 * Rules (all must pass; returns `false` on the first violation):
 *   1. Every question's `answer` must be present — non-null/undefined, a
 *      non-blank string, or a non-empty array.
 *   2. When a question has `isTextAreaMandatory === true`, the
 *      `free_text_answer.answer` must also be present (non-blank).
 *   3. If a question's current `answer` differs from the original answer
 *      recorded in `originalAnswersByName` (keyed by `question.name`),
 *      the `answerChangedReason` must be present (non-blank).
 *
 * @param questions             - Questions to validate (e.g. the visible chain)
 * @param originalAnswersByName - Lookup of the original answer per
 *                                `question.name`. Pass an empty map (or
 *                                omit) to skip rule #3.
 * @returns `true` when every rule holds for every question; `false` otherwise
 */
export function validateQuestionnaireAnswers(
  questions: QuestionForUI[],
  originalAnswersByName: Map<string, Answer> = new Map(),
): boolean {
  for (const q of questions) {
    if (!hasMeaningfulValue(q.answer)) return false;

    if (q.isTextAreaMandatory && !hasMeaningfulValue(q.free_text_answer.answer))
      return false;

    if (originalAnswersByName.size > 0) {
      const originalAnswer = originalAnswersByName.get(q.name);
      if (
        !areAnswersEqual(originalAnswer, q.answer) &&
        !hasMeaningfulValue(q.answerChangedReason)
      ) {
        return false;
      }
    }
  }
  return true;
}
